/**
 * ResponseThumbnail component specifically for handling enquiry response PDF and Image previews.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Services from '../../services';
import ImagePreview from '../ImagePreview';

const fetchResponseBodyThumbnail = async ({
  enquiryId,
  responseId,
  shareId
}: {
  enquiryId: number;
  responseId: number;
  shareId: string;
}): Promise<{ thumbnail: string }> => {
  try {
    const response = await Services.enquiry.getResponseBodyThumbnail(
      enquiryId,
      responseId,
      shareId
    );

    return response;
  } catch (error) {
    throw new Error('Failed to fetch response body thumbnail');
  }
};

const fetchResponseAttachmentThumbnail = async ({
  enquiryId,
  responseId,
  fileId,
  shareId
}: {
  enquiryId: number;
  responseId: number;
  fileId: number;
  shareId: string;
}): Promise<{ thumbnail: string }> => {
  try {
    const response = await Services.enquiry.getResponseAttachmentThumbnail(
      enquiryId,
      responseId,
      fileId,
      shareId
    );

    return response;
  } catch (error) {
    throw new Error('Failed to fetch response attachment thumbnail');
  }
};

interface ResponseThumbnailProps {
  fileType: 'pdf' | 'image' | 'body';
  responseId?: number;
  enquiryId?: number;
  fileId?: number;
  containerWidth?: number;
  containerHeight?: number;
  className?: string;
  style?: React.CSSProperties;
}

const ResponseThumbnail: React.FC<ResponseThumbnailProps> = ({
  fileType,
  responseId,
  enquiryId = 0,
  fileId = 0,
  containerWidth = 200,
  containerHeight = 250,
  className = '',
  style = {}
}) => {
  const history = useHistory();
  const queryParams = new URLSearchParams(history?.location.search);
  const shareId = queryParams.get('shareId') || '';

  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [isLoadingThumbnail, setIsLoadingThumbnail] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isMountedRef = useRef<boolean>(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any ongoing requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!isMountedRef.current) return;
    if (!responseId) return;

    // Cancel any previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();

    // Fetch thumbnail base64 string
    const fetchThumbnail = async () => {
      setIsLoadingThumbnail(true);
      setError('');

      try {
        const response =
          fileType === 'body'
            ? await fetchResponseBodyThumbnail({
                enquiryId,
                responseId,
                shareId
              })
            : await fetchResponseAttachmentThumbnail({
                enquiryId,
                responseId,
                fileId,
                shareId
              });

        if (!isMountedRef.current) return;

        if (response.thumbnail) {
          setThumbnail(response.thumbnail);
        } else {
          setError('No thumbnail returned from server');
        }
      } catch (error) {
        if (!isMountedRef.current) return;

        // Don't show error if request was aborted (component unmounted or new request started)
        if (error instanceof Error && error.name === 'AbortError') return;

        console.error('Failed to fetch thumbnail:', error);
        setError(
          error instanceof Error ? error.message : 'Failed to load thumbnail'
        );
      } finally {
        if (isMountedRef.current) {
          setIsLoadingThumbnail(false);
        }
      }
    };

    fetchThumbnail();
  }, [responseId, fileId, shareId, fileType, enquiryId]);

  const isLoading = (responseId && isLoadingThumbnail) || Boolean(error);

  if (error) {
    return (
      <div
        className={`response-pdf-preview-wrapper ${className}`}
        style={{
          width: containerWidth,
          height: containerHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f4f4f4',
          color: '#d32f2f',
          fontSize: '12px',
          textAlign: 'center',
          padding: '8px',
          ...style
        }}
      >
        Error loading thumbnail
      </div>
    );
  }

  return (
    <div className={`response-pdf-preview-wrapper ${className}`} style={style}>
      <ImagePreview
        imageUrl={`data:image/jpeg;base64,${thumbnail}`}
        isLoading={isLoading}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
      />
    </div>
  );
};

ResponseThumbnail.displayName = 'ResponseThumbnail';

export default ResponseThumbnail;
